"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
interface MarkdownToolbarProps {
  editableRef: React.RefObject<HTMLDivElement | null>;
  onContentChange: () => void;
  isPreview?: boolean;
  onTogglePreview?: () => void;
}
interface FormatAction {
  label: string;
  title: string;
  prefix: string;
  suffix: string;
}
const FORMAT_ACTIONS: FormatAction[] = [{
  label: "B",
  title: "Bold",
  prefix: "**",
  suffix: "**"
}, {
  label: "I",
  title: "Italic",
  prefix: "_",
  suffix: "_"
}, {
  label: "S",
  title: "Strikethrough",
  prefix: "~~",
  suffix: "~~"
}, {
  label: "</>",
  title: "Inline code",
  prefix: "`",
  suffix: "`"
}];
function insertMarkdownSyntax(editableRef: React.RefObject<HTMLDivElement | null>, prefix: string, suffix: string, placeholder: string): void {
  const el = editableRef.current;
  if (!el) return;
  el.focus();
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const selectedText = range.toString();
  const textToWrap = selectedText || placeholder;
  range.deleteContents();
  const textNode = document.createTextNode(`${prefix}${textToWrap}${suffix}`);
  range.insertNode(textNode);

  // Position cursor after inserted text (or select the placeholder)
  const newRange = document.createRange();
  if (selectedText) {
    newRange.setStartAfter(textNode);
    newRange.collapse(true);
  } else {
    // Select the placeholder text so user can type over it
    newRange.setStart(textNode, prefix.length);
    newRange.setEnd(textNode, prefix.length + placeholder.length);
  }
  selection.removeAllRanges();
  selection.addRange(newRange);
}
function insertList(editableRef: React.RefObject<HTMLDivElement | null>, ordered: boolean): void {
  const el = editableRef.current;
  if (!el) return;
  el.focus();
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const selectedText = range.toString();
  let listText: string;
  if (selectedText) {
    // Convert selected lines into a list
    const lines = selectedText.split("\n").filter(l => l.trim());
    listText = lines.map((line, i) => ordered ? `${i + 1}. ${line}` : `- ${line}`).join("\n");
  } else {
    listText = ordered ? "1. item" : "- item";
  }
  range.deleteContents();
  const textNode = document.createTextNode(listText);
  range.insertNode(textNode);
  const newRange = document.createRange();
  if (selectedText) {
    newRange.setStartAfter(textNode);
    newRange.collapse(true);
  } else {
    // Select "item" placeholder
    const prefixLen = ordered ? 3 : 2;
    newRange.setStart(textNode, prefixLen);
    newRange.setEnd(textNode, prefixLen + 4);
  }
  selection.removeAllRanges();
  selection.addRange(newRange);
}
function insertLink(editableRef: React.RefObject<HTMLDivElement | null>): void {
  const el = editableRef.current;
  if (!el) return;
  el.focus();
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const selectedText = range.toString();
  range.deleteContents();
  const linkText = selectedText || "text";
  const textNode = document.createTextNode(`[${linkText}](url)`);
  range.insertNode(textNode);

  // Select "url" so user can type the URL
  const newRange = document.createRange();
  const urlStart = linkText.length + 3; // after "[text](" — index of "u" in "url"
  newRange.setStart(textNode, urlStart);
  newRange.setEnd(textNode, urlStart + 3); // select "url"
  selection.removeAllRanges();
  selection.addRange(newRange);
}
export default function MarkdownToolbar(t0) {
  const $ = _c(45);
  const {
    editableRef,
    onContentChange,
    isPreview,
    onTogglePreview
  } = t0;
  let t1;
  if ($[0] !== editableRef || $[1] !== onContentChange) {
    t1 = action => {
      const placeholder = action.title.toLowerCase();
      insertMarkdownSyntax(editableRef, action.prefix, action.suffix, placeholder);
      onContentChange();
    };
    $[0] = editableRef;
    $[1] = onContentChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFormat = t1;
  let t2;
  if ($[3] !== editableRef || $[4] !== onContentChange) {
    t2 = () => {
      insertLink(editableRef);
      onContentChange();
    };
    $[3] = editableRef;
    $[4] = onContentChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleLink = t2;
  let t3;
  if ($[6] !== editableRef || $[7] !== onContentChange) {
    t3 = ordered => {
      insertList(editableRef, ordered);
      onContentChange();
    };
    $[6] = editableRef;
    $[7] = onContentChange;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleList = t3;
  let t4;
  if ($[9] !== handleFormat || $[10] !== isPreview) {
    t4 = FORMAT_ACTIONS.map(action_0 => <button key={action_0.label} type="button" title={action_0.title} disabled={isPreview} onMouseDown={e => {
      e.preventDefault();
      if (!isPreview) {
        handleFormat(action_0);
      }
    }} className={`flex h-7 w-7 items-center justify-center rounded text-xs ${isPreview ? "dark:text-ds-text-subtle cursor-not-allowed text-zinc-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 hover:text-zinc-800 dark:hover:text-zinc-200"}`}>{action_0.label === "B" ? <span className="font-bold">B</span> : action_0.label === "I" ? <span className="italic">I</span> : action_0.label === "S" ? <span className="line-through">S</span> : <span className="text-3xs font-mono">{"</>"}</span>}</button>);
    $[9] = handleFormat;
    $[10] = isPreview;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== handleLink || $[13] !== isPreview) {
    t5 = e_0 => {
      e_0.preventDefault();
      if (!isPreview) {
        handleLink();
      }
    };
    $[12] = handleLink;
    $[13] = isPreview;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = `flex h-7 items-center justify-center rounded px-1.5 text-xs ${isPreview ? "dark:text-ds-text-subtle cursor-not-allowed text-zinc-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 hover:text-zinc-800 dark:hover:text-zinc-200"}`;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5"><path fillRule="evenodd" d="M8.914 6.025a.75.75 0 0 1 1.06 0 3.5 3.5 0 0 1 0 4.95l-2 2a3.5 3.5 0 0 1-4.95-4.95l1.25-1.25a.75.75 0 0 1 1.06 1.06l-1.25 1.25a2 2 0 1 0 2.83 2.83l2-2a2 2 0 0 0 0-2.83.75.75 0 0 1 0-1.06Z" clipRule="evenodd" /><path fillRule="evenodd" d="M7.086 9.975a.75.75 0 0 1-1.06 0 3.5 3.5 0 0 1 0-4.95l2-2a3.5 3.5 0 0 1 4.95 4.95l-1.25 1.25a.75.75 0 0 1-1.06-1.06l1.25-1.25a2 2 0 1 0-2.83-2.83l-2 2a2 2 0 0 0 0 2.83.75.75 0 0 1 0 1.06Z" clipRule="evenodd" /></svg>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isPreview || $[17] !== t5 || $[18] !== t6) {
    t8 = <button type="button" title="Insert link" disabled={isPreview} onMouseDown={t5} className={t6}>{t7}</button>;
    $[16] = isPreview;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== handleList || $[21] !== isPreview) {
    t9 = e_1 => {
      e_1.preventDefault();
      if (!isPreview) {
        handleList(false);
      }
    };
    $[20] = handleList;
    $[21] = isPreview;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = `flex h-7 w-7 items-center justify-center rounded text-xs ${isPreview ? "dark:text-ds-text-subtle cursor-not-allowed text-zinc-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 hover:text-zinc-800 dark:hover:text-zinc-200"}`;
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5"><path fillRule="evenodd" d="M2.5 4a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1ZM5 3.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1-.5-.5ZM5 8a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8A.5.5 0 0 1 5 8Zm0 4.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1-.5-.5ZM2.5 8.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1ZM2.5 13a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z" clipRule="evenodd" /></svg>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== isPreview || $[25] !== t10 || $[26] !== t9) {
    t12 = <button type="button" title="Bullet list" disabled={isPreview} onMouseDown={t9} className={t10}>{t11}</button>;
    $[24] = isPreview;
    $[25] = t10;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== handleList || $[29] !== isPreview) {
    t13 = e_2 => {
      e_2.preventDefault();
      if (!isPreview) {
        handleList(true);
      }
    };
    $[28] = handleList;
    $[29] = isPreview;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = `flex h-7 w-7 items-center justify-center rounded text-xs ${isPreview ? "dark:text-ds-text-subtle cursor-not-allowed text-zinc-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 hover:text-zinc-800 dark:hover:text-zinc-200"}`;
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5"><path fillRule="evenodd" d="M2 3.5c0-.2.1-.4.3-.5h.9l.1.5V5h.5v.7H2V5h.5V4H2v-.5ZM5 3.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1-.5-.5ZM5 8a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8A.5.5 0 0 1 5 8Zm0 4.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1-.5-.5ZM2 8c0-.3.1-.5.3-.6.2-.2.5-.2.7-.1l.6.4.4-.3.4.5-.5.4.5.5-.4.4-.5-.4-.3.2-.2-.1V8ZM2 12.3c0-.2.2-.3.3-.3h1v.6h-.6c0 .1.1.3.3.3s.4.2.4.4c0 .3-.2.5-.6.5-.3 0-.5-.1-.6-.3l.3-.4c.1.1.2.2.3.2s.1 0 .1-.1-.1-.1-.2-.1h-.3v-.5h.3c.1 0 .1 0 .1-.1 0 0 0-.1-.1-.1h-.4v-.1Z" clipRule="evenodd" /></svg>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== isPreview || $[33] !== t13 || $[34] !== t14) {
    t16 = <button type="button" title="Numbered list" disabled={isPreview} onMouseDown={t13} className={t14}>{t15}</button>;
    $[32] = isPreview;
    $[33] = t13;
    $[34] = t14;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== isPreview || $[37] !== onTogglePreview) {
    t17 = onTogglePreview && <><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 mx-1 h-4 w-px" /><button type="button" title={isPreview ? "Edit" : "Preview"} onMouseDown={e_3 => {
        e_3.preventDefault();
        onTogglePreview();
      }} className={`flex h-7 items-center justify-center rounded px-2 text-xs ${isPreview ? "bg-ds-neutral-200 text-zinc-800 dark:bg-zinc-600 dark:text-zinc-100" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 hover:text-zinc-800 dark:hover:text-zinc-200"}`}>{isPreview ? "Edit" : "Preview"}</button></>;
    $[36] = isPreview;
    $[37] = onTogglePreview;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t12 || $[40] !== t16 || $[41] !== t17 || $[42] !== t4 || $[43] !== t8) {
    t18 = <div className="border-ds-border-default flex items-center gap-0.5 border-b px-2 py-1">{t4}{t8}{t12}{t16}{t17}</div>;
    $[39] = t12;
    $[40] = t16;
    $[41] = t17;
    $[42] = t4;
    $[43] = t8;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}